import { I18n } from '@calendly/i18n';
import { CheckboxInput } from '@calendly/ui/components/checkbox-input';
import { Loader } from '@calendly/ui/components/loader';
import { MinimalButton } from '@calendly/ui/components/minimal-button';
import { BodyText } from '@calendly/ui/components/typography';
import classNames from 'classnames';
import { useEffect, useRef, forwardRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFetchManagedEventTypesQuery, selectAllManagedEventTypes } from '../api';
import { isTouchedSelector, selectedIdsSelector } from './selectors';
import { clearSelection, setSelectedIds, toggleSelection } from './slice';
import { Marker } from './styles';
const controlsCss = "c9l7qov";
const Controls = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(controlsCss, props.className)} />;
});
const listCss = "l18b1d9h";
const List = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(listCss, props.className)} />;
});
const labelCss = "lbldrtv";
const Label = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'label';
  return <Component ref={ref} {...restProps} className={classNames(labelCss, props.className)} />;
});
const descriptionCss = "dlryqc0";
const Description = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(descriptionCss, props.className)} />;
});
const errorMessageCss = "efz5ebt";
const ErrorMessage = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'p';
  return <Component ref={ref} {...restProps} className={classNames(errorMessageCss, props.className)} />;
});
const Card = ({
  id,
  name,
  color,
  duration,
  slug
}) => {
  const dispatch = useDispatch();
  const selectedIds = useSelector(selectedIdsSelector);
  const toggleManagedEvent = () => dispatch(toggleSelection(id));
  return <div>
      <Label>
        <CheckboxInput spacingTop={4} checked={selectedIds.includes(id)} onChange={toggleManagedEvent} />
        <Marker color={color} />
        <Description>
          <BodyText weight="bold">{name}</BodyText>
          <BodyText>
            /
            {slug}
          </BodyText>
          <BodyText color="muted">{duration}</BodyText>
        </Description>
      </Label>
    </div>;
};
export const ManagedEventsSection = () => {
  const dispatch = useDispatch();
  const {
    data: {
      collection
    } = {},
    isFetching
  } = useFetchManagedEventTypesQuery({
    onlyOrgOwned: true,
    pageSize: 1000
  });
  const managedEvents = collection ? selectAllManagedEventTypes(collection) : [];
  const managedEventsCount = managedEvents.length;
  const selectedCount = useSelector(selectedIdsSelector).length;
  const isTouched = useSelector(isTouchedSelector);
  const hasError = isTouched && selectedCount < 1;
  const selectAll = () => dispatch(setSelectedIds(managedEvents.map(({
    id
  }) => id)));
  const selectAllRef = useRef();
  useEffect(() => {
    selectAllRef.current.focus();
  }, []);
  return <>
      {hasError && <ErrorMessage>
        {I18n.t('publisher.organization.members.bulk_managed_events_applying.no_selections_error')}
      </ErrorMessage>}
      <Controls>
        <MinimalButton ref={selectAllRef} onClick={selectAll}>
          {I18n.t('publisher.organization.members.bulk_managed_events_applying.select_all', {
          count: managedEventsCount
        })}
        </MinimalButton>
        <MinimalButton onClick={() => dispatch(clearSelection())}>
          {I18n.t('publisher.organization.members.bulk_managed_events_applying.clear_all')}
        </MinimalButton>
      </Controls>
      <List>
        {isFetching && <Loader data-testid="loader" />}
        {!isFetching && managedEvents.map(({
        id,
        name,
        color,
        duration,
        slug
      }) => <Card id={id} key={id} name={name} color={color} duration={duration} slug={slug} />)}
      </List>
    </>;
};

require("./managed_events_section.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./managed_events_section.js");